//Hooks
import { get } from 'svelte/store';
//Formating
import { format } from 'svelte-i18n';
//Types
import type { LabelProps } from '@Shelly/shelly-design-system/index';
import type { WebHooksParams } from 'src/types/requests/settings/webhooks';
import {
	CONDITIONS_RULES,
	type Condition,
	type ConditionToken,
	type I18nCtx,
} from '../rules/webhook_conditions_rules';

const tokenizeCondition = (expr: string): ConditionToken[] => {
	return (expr || '').split(/\s*(\|\||&&)\s*/g).filter(Boolean);
};

const buildEventLabel = (hook: WebHooksParams): string | null => {
	const $format = get(format);
	if (/(measurement|change)/.test(hook.event)) {
		const label = hook.title ? `${hook.title} ` : '';
		const state = /(measurement)/.test(hook.event)
			? $format('action_on_measure', { default: 'on measure' })
			: $format('action_on_change', { default: 'on change' });
		return `${label}${state}`;
	} else if (hook?.event) {
		return $format(`option_${hook.event.replace('.', '_').replace(/^(cct|rgbcct)/, 'light')}`, {
			default: hook.event.replace(/(\.|_)/g, ' '),
		});
	}
	return null;
};

const buildComponentLabel = (hook: WebHooksParams, labelList: LabelProps[]): LabelProps[] => {
	if (hook?.components?.length) {
		const component = hook.components.find((i) => i.cid == hook.cid);
		if (component) {
			labelList.push({
				icon: component.icon,
				value: component.title,
			});
		}
	}
	return labelList;
};

const buildWebhookAdditionalConditionLabels = (
	hook: WebHooksParams,
	labelList: LabelProps[],
): LabelProps[] => {
	const $format = get(format);
	const ctx: I18nCtx = {
		translate: (k, d) => $format(k, { default: d }),
	};
	const tokens = tokenizeCondition(hook.condition);
	for (const token of tokens) {
		const condition = token.trim().split(' ');
		if (condition[0] === '&&') {
			labelList.push({
				value: ctx.translate('and', 'and').toUpperCase(),
			});
			continue;
		} else if (condition[0] === '||') {
			labelList.push({
				value: ctx.translate('or', 'or').toUpperCase(),
			});
			continue;
		}
		const conditionRule: Condition = {
			event: condition[0],
			operant: condition[1] as Condition['operant'],
			value: condition[2],
		};
		CONDITIONS_RULES.filter(
			(r) => r.webhooks && r.webhooks.includes(hook.event) && r.when(conditionRule),
		).forEach((r) => {
			if (r.buildComponentLabel && r.buildComponentLabel(hook)) {
				labelList = buildComponentLabel(hook, labelList);
			}
			const value = r.conditionlabel(conditionRule, ctx, hook);
			labelList.push({
				icon: r.icon(hook, conditionRule),
				value: (value || '').replace('===', '=').replace('!==', '!='),
				iconColor: r.iconColor ? r.iconColor(hook) : null,
			});
		});
	}
	return labelList;
};

export const buildWebhookConditionLabels = (
	hook: WebHooksParams,
	labelList: LabelProps[],
): LabelProps[] => {
	if (hook.condition) {
		return buildWebhookAdditionalConditionLabels(hook, labelList);
	}
	CONDITIONS_RULES.filter((r) => r.webhooks && r.webhooks.includes(hook.event)).forEach((r) => {
		if (r.buildComponentLabel && r.buildComponentLabel(hook)) {
			labelList = buildComponentLabel(hook, labelList);
		}
		labelList.push({
			icon: hook.icon || r.icon(hook),
			value: r.buildEventLabel ? r.buildEventLabel(hook) : buildEventLabel(hook),
			iconColor: r?.iconColor ? r.iconColor(hook) : null,
		});
	});
	return labelList;
};
